import { CommonModule } from '@angular/common';
import {
  ChangeDetectionStrategy,
  Component,
  EventEmitter,
  Input,
  Output,
  OnInit,
} from '@angular/core';
import {
  ReactiveFormsModule,
  FormsModule,
  FormControl,
  Validators,
} from '@angular/forms';
import { MatButtonModule } from '@angular/material/button';
import { MatChipsModule } from '@angular/material/chips';
import { MatDatepickerModule } from '@angular/material/datepicker';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatIconModule } from '@angular/material/icon';
import { MatInputModule } from '@angular/material/input';
import { MatSelectModule } from '@angular/material/select';
import { provideNativeDateAdapter } from '@angular/material/core';
import { IttWrapperComponent } from '../itt-wrapper/itt-wrapper.component';
import { NgToastService } from 'ng-angular-popup';
import { TenderingService } from '@/app/services/tendering-services/tendering.service';
import { DatePipe } from '@angular/common';
import { InputTimepickerComponent } from '@/app/components/global/inputs/input-timepicker/input-timepicker.component';
import { ActivatedRoute, Router } from '@angular/router';
import { RfpLabelService } from '../../../rfp-labels.service';

@Component({
  selector: 'app-itt-tender-submission-guidlines',
  standalone: true,
  providers: [provideNativeDateAdapter()],
  imports: [
    MatChipsModule,
    MatButtonModule,
    MatSelectModule,
    FormsModule,
    CommonModule,
    ReactiveFormsModule,
    MatFormFieldModule,
    MatInputModule,
    MatIconModule,
    MatDatepickerModule,
    InputTimepickerComponent,
  ],
  changeDetection: ChangeDetectionStrategy.OnPush,
  templateUrl: './itt-tender-submission-guidlines.component.html',
  styleUrl: './itt-tender-submission-guidlines.component.css',
})
export class IttTenderSubmissionGuidlinesComponent implements OnInit {
  @Output() tenderSubmissionDataEmitter: EventEmitter<any> =
    new EventEmitter<any>();
  @Input() ITTDeveloperData: any = [];

  projectId: number;
  projectName: string;

  tenderSubmissionDeadlineDateControl = new FormControl(
    '',
    Validators.required
  );
  tenderSubmissionDeadlineTimeControl = new FormControl(
    '',
    Validators.required
  );
  tenderSubmissionDeadlineAddressControl = new FormControl(
    '',
    Validators.required
  );

  closeModal = true;
  labels: any;

  constructor(
    private toast: NgToastService,
    private tenderingService: TenderingService,
    private IttWrapperComponent: IttWrapperComponent,
    private route: ActivatedRoute,
    private rfpLabelService: RfpLabelService,
    private router: Router,
  ) {}

  ngOnInit(): void {
    this.route.url.subscribe(() => {
      const currentRoute = this.router.url; // Get full URL path
      this.rfpLabelService.updateLabelsFromRoute(currentRoute);
    });
    this.rfpLabelService.labels$.subscribe((labels) => {
      this.labels = labels;
    });
    this.route.queryParams.subscribe((params) => {
      this.projectName = params['projectName'];
    });
    this.route.paramMap.subscribe((params) => {
      this.projectId = params.get('projectId')
        ? parseInt(params.get('projectId'))
        : null;
    });

    /**
     * NOTE: On edit or when we go back and forth between wizards, we will
     *  set initial value if it exists
     */
    if (
      this.ITTDeveloperData.submission_address &&
      this.ITTDeveloperData.submission_deadline_date &&
      this.ITTDeveloperData.submission_deadline_time
    ) {
      this.tenderSubmissionDeadlineAddressControl.setValue(
        this.ITTDeveloperData.submission_address
      );
      this.tenderSubmissionDeadlineDateControl.setValue(
        this.ITTDeveloperData.submission_deadline_date
      );
      this.tenderSubmissionDeadlineTimeControl.setValue(
        this.ITTDeveloperData.submission_deadline_time.slice(0, 5)
      );
    }
  }

  formatTime(input: string): string {
    // Extract time part from the input
    const [, time] = input.split('T');
    const [hours, minutes] = time.split(':').map(Number);

    // Ensure 24-hour format with padded zeros
    return `${hours.toString().padStart(2, '0')}:${minutes
      .toString()
      .padStart(2, '0')}`;
  }

  prepareDataToSubmit() {
    this.tenderSubmissionDeadlineAddressControl.markAsTouched();
    this.tenderSubmissionDeadlineDateControl.markAsTouched();
    this.tenderSubmissionDeadlineTimeControl.markAsTouched();

    if (
      this.tenderSubmissionDeadlineAddressControl.value &&
      this.tenderSubmissionDeadlineDateControl.value &&
      this.tenderSubmissionDeadlineTimeControl.value
    ) {
      const datePipe = new DatePipe('en-US');
      const date = datePipe.transform(
        this.tenderSubmissionDeadlineDateControl.value,
        'MM/dd/yyyy'
      );
      const data = {
        submission_date: date,
        submission_time: this.tenderSubmissionDeadlineTimeControl.value,
        submission_address: this.tenderSubmissionDeadlineAddressControl.value,
      };
      return data;
    } else {
      this.toast.warning('Please fill the required fields', 'warning', 4000);
    }
  }

  moveToPreviousStep() {
    this.IttWrapperComponent.previousStep();
  }

  moveToNextStep() {
    const dataToSubmit = this.prepareDataToSubmit();

    if (dataToSubmit) {
      this.tenderingService
        .storeDeveloperITTSubmissionGuidelinesData(dataToSubmit, this.projectId)
        .subscribe({
          next: (response: any) => {
            if (response?.meta?.success) {
              this.toast.success(
                'Tender Submission details saved successfully',
                'SUCCESS'
              );
              this.tenderSubmissionDataEmitter.emit(response?.data);
              this.IttWrapperComponent.nextStep();
              return;
            }
            this.toast.danger(
              'Work packages details could not be saved',
              'DANGER',
              4000
            );
          },
          error: (error: any) => {
            console.error('Failed to submit:', error);
            this.toast.danger(error.error.message, 'DANGER', 4000);
          },
        });
    }
  }
}
